import { DatePipe } from '@angular/common';
import { Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { EmployeeCaseOption } from '@core/models';
import { HoursRequestCaseInfo } from '@core/models/leave-admin/submit-hours-request-dialog.model';
import { LeaveHoursForm } from '@core/models/shared/submit-hours-form-array.model';
import { LayoutService, NotificationService } from '@core/services';
import { parseDateHours } from '@shared/helpers/data-parsers.helpers';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-submit-hours-form-array',
    templateUrl: './submit-hours-form-array.component.html',
    styleUrl: './submit-hours-form-array.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SubmitHoursFormArrayComponent {
  static readonly componentName = 'SubmitHoursFormArrayComponent';
  
  @Input() leaveHoursFormArray: FormArray<FormGroup<LeaveHoursForm>>;
  @Input() group: FormGroup;
  @Input() sysText: any;
  @Input() showLeavePickList: boolean;
  @Input() employeeCaseControl: FormControl<EmployeeCaseOption>; //nullable
  @Input() set caseInformationInput(value: HoursRequestCaseInfo) {
    this.caseInformation = value;
    this.someBanksFullDayIncrementSub.next(value?.someBanksFullDayIncrement ?? false);
    this.showFullDayControl = value?.someBanksFullDayIncrement ?? false;
  }

  @ViewChild('addHoursButton', { read: ElementRef }) private addHoursButton: ElementRef<HTMLButtonElement>;

  public disableClipboardButton: boolean = false;
  public showFullDayControl: boolean = false;

  private someBanksFullDayIncrementSub = new BehaviorSubject<boolean>(false);
  private $someBanksFullDayIncrement = this.someBanksFullDayIncrementSub.asObservable();
  private caseInformation: HoursRequestCaseInfo;
  private showDateMatchHint: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get isMobile(): boolean {
    return this.layoutService.isMobile;
  }

  constructor(
    private layoutService: LayoutService,
    private notifyService: NotificationService,
    private fb: FormBuilder,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.addLeaveHoursFormGroup();
    // clipboard readText() is currently not supported 
    // by Firefox, disable button if not supported 
    if (!navigator['clipboard'] || !(typeof navigator['clipboard'].readText === "function")) {
      this.disableClipboardButton = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onRemoveHoursSubmittal(index: number): void {
    // the pressed button goes with its row
    this.addHoursButton.nativeElement.focus();
    this.leaveHoursFormArray.removeAt(index);
  }

  public onPasteIntoField(event: ClipboardEvent, index: number): void {
    if (event.type === "paste"){
      this.handleHoursPaste(event.clipboardData.getData("text"), index, true);
    }
  }

  public onAddHoursSubmittal() {
    this.addLeaveHoursFormGroup();
  }

  public onAddFromClipboard(): void {
    const formGroup = this.leaveHoursFormArray.controls[0];
    const firstRowEmpty = !formGroup.value.requestDate && !formGroup.value.requestHours ;
    const startingIndex = (firstRowEmpty 
      && this.leaveHoursFormArray.controls.length == 1) ? 0 : this.leaveHoursFormArray.controls.length;
    navigator['clipboard'].readText()
      .then(clipText => {
        this.handleHoursPaste(clipText, startingIndex);
      })
      .catch(() => {
        this.notifyService.showErrorToast(this.sysText.clipboardError);
      });
  }

  private handleHoursPaste(
    text: string, 
    startingIndex: number, 
    pasteIntoRow: boolean = false): void {
    if (pasteIntoRow) {
      for (let i = (this.leaveHoursFormArray.controls.length - 1); i > startingIndex; i--) {
        this.onRemoveHoursSubmittal(i);
      }
    }
    let dontAddFormGroupRow = startingIndex == 0 || pasteIntoRow;
    const dateHoursArray = parseDateHours(text);
    dateHoursArray.forEach((dh, i) => {
      if (!dontAddFormGroupRow) {
        this.addLeaveHoursFormGroup();
      }
      dontAddFormGroupRow = false;
      setTimeout(() => {
        const formGroup = this.leaveHoursFormArray.at(i + startingIndex) as FormGroup<LeaveHoursForm>;
        formGroup.patchValue({
          requestDate: dh.date,
          requestHours: +dh.hours
        });
        formGroup.markAllAsTouched();
      }, 0);
    })
  }

  public addLeaveHoursFormGroup() {
    const requestHoursGroup = this.fb.group<LeaveHoursForm>({
      requestDate: this.fb.control(null, Validators.required),
      requestHours: this.fb.control(null, [Validators.required, Validators.pattern(/^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/)]),
      isFullDay: this.fb.control(null),
      fullDayIncrement: this.fb.control(null),
      notFullDayWarn: this.fb.control(null),
      sameDateWarn: this.fb.control(null),
      workdayWarn: this.fb.control(null),
      workdayHoursWarn: this.fb.control(null)
    });

    requestHoursGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formValidation(this.caseInformation);
      });

    // set up the follwoing subscriptions (note they only apply if some banks 
    // restict to full day inrements but set them up now since case can change):
    //  - on date selection grab the workday hours from the case calendar
    //  - on hours field, if hours equals workday hours check full day box
    //  - on full day checkbox, if checked set horus to full workday value
    requestHoursGroup.controls.requestDate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleFullDayDateSelection(requestHoursGroup);
      });
    requestHoursGroup.controls.requestDate.setValue(null); // trigger inital change
    requestHoursGroup.controls.requestHours.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.handleFullDayHoursChange(requestHoursGroup);
      });
    requestHoursGroup.controls.isFullDay.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.handleFullDayChange(requestHoursGroup);
      });

    // if any banks restrict to full work days disable full day checkbox
    // until a date is selected (to determine if that date is a workday and if so 
    // how many workday hours it is) 
    if (this.someBanksFullDayIncrementSub.value) {
      requestHoursGroup.controls.isFullDay.disable();
    }

    // if employee picklist is available set up additional subscriptions
    if (this.showLeavePickList) {
      // ...if adding a group and no ee selected disable group
      if (!this.employeeCaseControl?.value) {
        requestHoursGroup.disable({emitEvent: false});
      }

      // ...listen to changes to someBanksFullDayIncrement (based off ee selection)
      this.$someBanksFullDayIncrement
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            // if some banks restrict run date selection for existing dates
            this.handleFullDayDateSelection(requestHoursGroup);
            this.handleFullDayHoursChange(requestHoursGroup);
            // if no date selected disable checkbox
            if (requestHoursGroup.controls.isFullDay.enabled
            && !requestHoursGroup.controls.requestDate.value) {
              requestHoursGroup.controls.isFullDay.disable({emitEvent: false});
            }
          }
        });

      // ...if ee value cleared disable group
      this.employeeCaseControl?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (!res) {
            requestHoursGroup.disable();
          } else {
            requestHoursGroup.enable();
          }
        });
    }

    this.leaveHoursFormArray.push(requestHoursGroup);
  }

  public formValidation(caseInformation: HoursRequestCaseInfo): void {
    this.showDateMatchHint = false;
    for (let i = 0; i < this.leaveHoursFormArray.controls.length; i++) {
      const formGroup = this.leaveHoursFormArray.controls[i];
      const requestDate = formGroup.controls.requestDate.value ? 
        new Date(formGroup.value.requestDate?.toDateString()) : null;
      this.checkDateFieldMatch(requestDate, formGroup, this.leaveHoursFormArray, i);
      this.checkDateSchedule(caseInformation, requestDate, formGroup);
    }
  }

  private checkDateSchedule(
    caseInformation: HoursRequestCaseInfo,
    requestDate: Date, 
    formGroup: FormGroup<LeaveHoursForm>,): void {
    // first clear any existing warnings
    formGroup.controls.workdayWarn.setValue(null, {emitEvent: false});
    formGroup.controls.workdayHoursWarn.setValue(null, {emitEvent: false});

    // then if an employee case is selected
    if (this.employeeCaseControl?.value?.caseId && requestDate) {
      const scheduleOnDate = caseInformation?.employeeSchedule.find((ees) => 
        this.datePipe.transform(new Date(ees.date), 'yyyy-MM-dd') === 
          this.datePipe.transform(new Date(requestDate), 'yyyy-MM-dd')
      );

      // ...handle date outside case timeframe
      if (requestDate < new Date(this.employeeCaseControl?.value?.startDate) 
        || requestDate > new Date(this.employeeCaseControl?.value?.endDate)) {
        formGroup.controls.workdayWarn
          .setValue(this.sysText.timeframeWarn, {emitEvent: false});
      }

      // ...handle non-workday(s) warning
      else if (caseInformation?.showWorkdayWarnings === true
        && scheduleOnDate?.isWorkday === false) {
        if (scheduleOnDate?.nonWorkdays) {
          let nonWorkdaysString = '';
          scheduleOnDate.nonWorkdays.forEach((nwd, i)=> {
            nonWorkdaysString += `"${nwd}"`;
            if (i != (scheduleOnDate.nonWorkdays.length - 1)) {
              nonWorkdaysString += ', ';
            }
          });
          formGroup.controls.workdayWarn
            .setValue(`${nonWorkdaysString} ${scheduleOnDate.nonWorkdays.length > 1 
              ? this.sysText.areNonWorkdays : this.sysText.isNonWorkday}`, {emitEvent: false});
        } else {
          formGroup.controls.workdayWarn.setValue(this.sysText.dateIsNonWorkday, {emitEvent: false});
        }
      }

      // ...handle workday hours exceeded warning
      else if (caseInformation?.showWorkdayHoursWarnings  == true 
        && scheduleOnDate?.isWorkday) {
        if (formGroup.controls.requestHours.value > scheduleOnDate.workdayHours) {
          const hoursExceededMessage: string = this.sysText.workdayHoursExceeded
            .replace("@[hours]", scheduleOnDate.workdayHours);
          formGroup.controls.workdayHoursWarn.setValue(hoursExceededMessage, {emitEvent: false});
        } else {
          formGroup.controls.workdayHoursWarn.setValue(null, {emitEvent: false});
        }

      }
      
      // ...handle not a workday warning
      else if (caseInformation?.showWorkdayWarnings === true 
        && !scheduleOnDate?.isWorkday) {
        formGroup.controls.workdayWarn.setValue(this.sysText.notWorkday, {emitEvent: false});
      }
    }
  }

  private checkDateFieldMatch(
    requestDate: Date, 
    formGroup: FormGroup<LeaveHoursForm>, 
    formArray: FormArray<FormGroup<LeaveHoursForm>>, 
    fieldIndex: number): void {
    let hasMatch = false;
    for (let i = 0; i < formArray.controls.length; i++) {
      if (fieldIndex != i) {
        const otherFormGroup = formArray.controls[i];
        const othersubmittedDate = new Date(otherFormGroup.value.requestDate?.toDateString());
        if (requestDate?.getTime() === othersubmittedDate.getTime()) {
          let controlVal = this.showDateMatchHint ? formGroup.value.requestDate : null;
          if (formGroup.controls.sameDateWarn.value != controlVal) {
            formGroup.controls.sameDateWarn.setValue(controlVal, {emitEvent: false});
          }
          this.showDateMatchHint = true;
          hasMatch = true;
          break;
        }
      }
    }
    if (!hasMatch && formGroup.controls.sameDateWarn.value != null) {
      formGroup.controls.sameDateWarn.setValue(null, {emitEvent: false});
    }
  }

  private handleFullDayDateSelection(formGroup: FormGroup<LeaveHoursForm>): void {
    if (!this.someBanksFullDayIncrementSub.value) {
      return;
    }
    const selectedDate = this.datePipe.transform(new Date(formGroup.controls.requestDate.value), 'yyyy-MM-dd');
    const caseCalDay = this.caseInformation.employeeSchedule?.find(cd => 
      this.datePipe.transform(new Date(cd.date), 'yyyy-MM-dd') == selectedDate);

    if (selectedDate && caseCalDay?.fullDayIncrement) {
      formGroup.controls.fullDayIncrement.setValue(caseCalDay?.fullDayIncrement), {emitEvent: false};
      formGroup.controls.isFullDay.enable({emitEvent: false});
    } else {
      formGroup.controls.isFullDay.setValue(false, {emitEvent: false});
      formGroup.controls.fullDayIncrement.setValue(null);
      formGroup.controls.isFullDay.disable({emitEvent: false});
    }

    // auto patch workday value on check when all banks restrict
    // to full day increments
    if (this.caseInformation.allBanksFullDayIncrement
      && caseCalDay?.fullDayIncrement) {
      formGroup.patchValue({
        requestHours: caseCalDay?.fullDayIncrement,
        isFullDay: true
      });
    }
    this.handleFullDayHoursChange(formGroup);
  }

  private handleFullDayHoursChange(formGroup: FormGroup<LeaveHoursForm>): void {
    if (!this.someBanksFullDayIncrementSub.value) {
      return;
    }
    const selectedDate = this.datePipe.transform(new Date(formGroup.controls.requestDate.value), 'yyyy-MM-dd');
    const caseCalDay = this.caseInformation.employeeSchedule?.find(cd => 
      this.datePipe.transform(new Date(cd.date), 'yyyy-MM-dd') == selectedDate);
    const hours = formGroup.controls.requestHours.value;

    // handle setting full day checkbox if hours equal full day workhours
    if (!!hours && hours == caseCalDay?.fullDayIncrement) {
      formGroup.controls.isFullDay.setValue(true, {emitEvent: false});
    } else if (formGroup.controls.isFullDay.value) {
      formGroup.controls.isFullDay.setValue(false, {emitEvent: false});
    }
    
    // handle 'not full day' warning
    if (this.caseInformation.allBanksFullDayIncrement
      && !!formGroup.controls.requestDate.value
      && !!formGroup.controls.requestHours.value
      && (!caseCalDay?.fullDayIncrement 
      || formGroup.controls.requestHours.value != caseCalDay?.fullDayIncrement)) {
      formGroup.controls.notFullDayWarn.setValue(true);
    } else {
      formGroup.controls.notFullDayWarn.setValue(false);
    }
  }

  private handleFullDayChange(formGroup: FormGroup<LeaveHoursForm>): void {
    if (!this.someBanksFullDayIncrementSub.value) {
      return;
    }
    const selectedDate = this.datePipe.transform(new Date(formGroup.controls.requestDate.value), 'yyyy-MM-dd');
    const caseCalDay = this.caseInformation.employeeSchedule?.find(cd => 
      this.datePipe.transform(new Date(cd.date), 'yyyy-MM-dd') == selectedDate);
    const isFullDay = formGroup.controls.isFullDay.value;
    if (isFullDay && caseCalDay?.fullDayIncrement) {
      formGroup.controls.requestHours.setValue(caseCalDay?.fullDayIncrement);
    } else if (!isFullDay 
      && !!caseCalDay?.fullDayIncrement
      && caseCalDay?.fullDayIncrement == formGroup.controls.requestHours.value) {
      formGroup.controls.isFullDay.setValue(true, {emitEvent: false});
    }
  }
}